import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Shield, Link2, Type } from 'lucide-react'
import { PageContainer } from '@/components/layout/PageContainer'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import { StorefrontSettingsGuard } from './StorefrontSettingsGuard'
import { useStorefrontTenantSettings } from './useStorefrontTenantSettings'
import type { TenantSettingsResponse } from '@/lib/api/controlPlaneClient'
import { useQueryClient } from '@tanstack/react-query'

const DEFAULT_CONSENT_TEXT =
  'เว็บไซต์นี้ใช้คุกกี้เพื่อปรับปรุงประสบการณ์การใช้งานของคุณ โดยการใช้งานเว็บไซต์นี้ต่อไป คุณยินยอมให้เราใช้คุกกี้ตามนโยบายความเป็นส่วนตัวของเรา'

export function PdpaSettingsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { settings, isLoading, updateAsync, isUpdating, queryKey } =
    useStorefrontTenantSettings()

  const [policyUrl, setPolicyUrl] = useState('')
  const [consentText, setConsentText] = useState('')

  useEffect(() => {
    if (settings) {
      setPolicyUrl(settings.pdpa_policy_url ?? '')
      setConsentText(settings.pdpa_consent_text ?? '')
    }
  }, [settings])

  const handleSave = async () => {
    try {
      await updateAsync({
        pdpa_policy_url: policyUrl.trim(),
        pdpa_consent_text: consentText.trim(),
      })
      toast.success('PDPA settings saved')
    } catch {
      toast.error('Failed to save PDPA settings')
    }
  }

  const hasChanges =
    settings &&
    (policyUrl.trim() !== (settings.pdpa_policy_url ?? '') ||
      consentText.trim() !== (settings.pdpa_consent_text ?? ''))

  return (
    <StorefrontSettingsGuard>
      <PageContainer>
        <div className="p-6 space-y-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full"
              onClick={() => navigate('/storefront/settings')}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h2 className="text-2xl font-semibold">PDPA / Privacy</h2>
              <p className="text-sm text-muted-foreground">
                Cookie consent banner for PDPA compliance
              </p>
            </div>
          </div>

          {isLoading ? (
            <Card>
              <CardContent className="p-6 space-y-4">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-5 w-40 mt-4" />
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-6 space-y-6">
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <Shield className="w-4 h-4 text-muted-foreground" />
                      Enable PDPA Consent Banner
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Show a cookie consent banner when customers first visit
                      your store. Required by Thailand&apos;s Personal Data
                      Protection Act (PDPA).
                    </p>
                  </div>
                  {isLoading ? (
                    <Skeleton className="w-9 h-5 rounded-full" />
                  ) : (
                    <Switch
                      checked={!!settings?.enable_pdpa_consent}
                      onCheckedChange={async (checked) => {
                        const previousData =
                          queryClient.getQueryData<TenantSettingsResponse>(
                            queryKey,
                          )
                        queryClient.setQueryData<
                          TenantSettingsResponse | undefined
                        >(queryKey, (old) =>
                          old
                            ? {
                                ...old,
                                enable_pdpa_consent: checked ? 1 : 0,
                              }
                            : old,
                        )
                        try {
                          await updateAsync({
                            enable_pdpa_consent: checked ? 1 : 0,
                          })
                          toast(
                            checked
                              ? 'PDPA consent banner enabled'
                              : 'PDPA consent banner disabled',
                          )
                        } catch {
                          queryClient.setQueryData(queryKey, previousData)
                          toast.error('Failed to save. Please try again.')
                        }
                      }}
                      disabled={isUpdating}
                      aria-label="Enable PDPA consent banner"
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-muted-foreground" />
                    Privacy Policy URL
                  </label>
                  <Input
                    placeholder="https://yourstore.com/privacy-policy"
                    value={policyUrl}
                    onChange={(e) => setPolicyUrl(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Link to your privacy policy page. Shown as a clickable link
                    in the consent banner.
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Type className="w-4 h-4 text-muted-foreground" />
                    Consent Banner Text
                  </label>
                  <Textarea
                    placeholder={DEFAULT_CONSENT_TEXT}
                    value={consentText}
                    onChange={(e) => setConsentText(e.target.value)}
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">
                    Custom message shown in the consent banner. Leave empty to
                    use the default Thai text.
                  </p>
                </div>

                {!consentText.trim() && (
                  <div className="rounded-lg border border-dashed p-3">
                    <p className="text-xs font-medium text-muted-foreground mb-1">
                      Default text (used when empty):
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {DEFAULT_CONSENT_TEXT}
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleSave}
                  disabled={isUpdating || !hasChanges}
                  className="w-full sm:w-auto"
                >
                  {isUpdating ? 'Saving...' : 'Save'}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </PageContainer>
    </StorefrontSettingsGuard>
  )
}
